import { lazyImport } from 'shared/routing/LazyRoute';
import { Navigate, Outlet, RouteObject } from 'react-router-dom';
import { ContentProgressIndicator } from 'shared/layout/ContentProgressIndicator';
import { useHasOnboardingPackets } from 'shared/modules/onboarding-packets/data/OnboardingPacketsAPI';
import { useFlag } from 'shared/settings/FeatureToggles';
import { OnboardingPacketsLayout } from './OnboardingPacketsLayout';
import type { OnboardingPacketsPageProps } from './OnboardingPacketsPage';
import type { OnboardingPacketFormPageProps } from './OnboardingPacketFormPage';

const OnboardingPacketsPage = lazyImport<OnboardingPacketsPageProps>(
  () => import('./OnboardingPacketsPage'),
  'OnboardingPacketsPage',
);

const OnboardingPacketFormPage = lazyImport<OnboardingPacketFormPageProps>(
  () => import('./OnboardingPacketFormPage'),
  'OnboardingPacketFormPage',
);

const OnboardingPacketViewPage = lazyImport(
  () => import('./view/OnboardingPacketViewPage'),
  'OnboardingPacketViewPage',
);

export const onboardingPacketsRoutes: RouteObject[] = [
  {
    path: 'onboarding-packets',
    element: <OnboardingPacketsOutlet />,
    children: [
      {
        path: ':packetGUID/form',
        element: <OnboardingPacketFormPage />,
      },
      {
        path: ':packetGUID/view',
        element: <OnboardingPacketViewRoute />,
      },
      {
        element: <OnboardingPacketsLayout />,
        children: [
          {
            path: 'not-submitted',
            element: <OnboardingPacketsPage status="pending" />,
          },
          {
            path: 'pending',
            element: <OnboardingPacketsPage status="submitted" />,
          },
          {
            path: 'accepted',
            element: <OnboardingPacketsPage status="accepted" />,
          },
          {
            path: 'not-accepted',
            element: <OnboardingPacketsPage status="not_accepted" />,
          },
          {
            index: true,
            element: <OnboardingPacketsPage />,
          },
        ],
      },
    ],
  },
];

function OnboardingPacketsOutlet() {
  const { hasOnboardingPackets, isLoading } = useHasOnboardingPackets();

  if (isLoading) {
    return <ContentProgressIndicator />;
  }

  if (!hasOnboardingPackets) {
    return <Navigate to="/" replace={true} />;
  }

  return <Outlet />;
}

function OnboardingPacketViewRoute() {
  const isPacketViewEnabled = useFlag(
    'superprotect_carrier_onboarding_packets_view_page',
  );

  if (isPacketViewEnabled) {
    return <OnboardingPacketViewPage />;
  }

  return <OnboardingPacketFormPage isInViewMode={true} />;
}
